import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PageHeader } from '../components/PageHeader';
import {
  Boxes, Wallet, TrendingUp, Truck, ShoppingCart, Plus, Download, Save, RefreshCw,
  CheckCircle2, Clock, AlertTriangle, FileText, Link as LinkIcon, Wifi, WifiOff, Send,
  PackageCheck, XCircle, Sparkles,
} from 'lucide-react';
import { formatDate } from '../lib/format';
import { Combobox } from '../components/Combobox';

const PHP = (v) => (typeof v === 'number' ? `₱${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');

const REASONS = [
  { v: 'routine', l: 'Routine' },
  { v: 'preventive', l: 'Preventive replacement' },
  { v: 'breakdown', l: 'Breakdown repair' },
  { v: 'inspection', l: 'Inspection finding' },
  { v: 'scheduled', l: 'Scheduled maintenance' },
  { v: 'other', l: 'Other' },
];

const PR_REASONS = [
  { v: 'low_stock', l: 'Low stock' },
  { v: 'preventive', l: 'Preventive replacement' },
  { v: 'breakdown', l: 'Breakdown repair' },
  { v: 'other', l: 'Other' },
];

const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const STATUS_TONE = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-blue-100 text-blue-800 border-blue-200',
  ordered: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  received: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  closed: 'bg-slate-200 text-slate-700 border-slate-300',
  rejected: 'bg-red-100 text-red-800 border-red-200',
};

const NEXT_LABEL = {
  pending: { to: 'approved', label: 'Approve', icon: CheckCircle2, tone: 'bg-blue-600 hover:bg-blue-700' },
  approved: { to: 'ordered', label: 'Mark Ordered', icon: Send, tone: 'bg-indigo-600 hover:bg-indigo-700' },
  ordered: { to: 'received', label: 'Mark Received', icon: PackageCheck, tone: 'bg-emerald-600 hover:bg-emerald-700' },
  received: { to: 'closed', label: 'Close', icon: CheckCircle2, tone: 'bg-slate-600 hover:bg-slate-700' },
};

const DRAFT_KEY = (kind) => `consumables_draft_${kind}`;

const Consumables = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get('tab') === 'requests' ? 'requests' : params.get('tab') === 'log' ? 'log' : 'overview';
  const [tab, setTab] = useState(initialTab);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [maint, setMaint] = useState([]);
  const [logs, setLogs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dropdowns, setDropdowns] = useState({ consumable_type: [], consumable_brand: [], consumable_unit: [] });
  const [online, setOnline] = useState(navigator.onLine);
  const [logOpen, setLogOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [transition, setTransition] = useState(null); // { pr, to }

  const canApproveReject = hasRole(user, 'super_admin', 'admin', 'approver');
  const canFulfill = hasRole(user, 'super_admin', 'admin');
  const canDelete = hasRole(user, 'super_admin', 'admin');

  const loadAll = useCallback(async () => {
    try {
      const [vs, ls, rs, sm] = await Promise.all([
        api.get('/vehicles'),
        api.get('/consumables'),
        api.get('/purchase-requests'),
        api.get('/consumables/summary'),
      ]);
      setVehicles(vs.data); setLogs(ls.data); setReqs(rs.data); setSummary(sm.data);
      // optional resources (best-effort)
      try { const us = await api.get('/users'); setUsers(us.data); } catch (e) { console.warn('Consumables users fetch failed:', e?.message || e); }
      try { const mm = await api.get('/maintenance'); setMaint(mm.data); } catch (e) { console.warn('Consumables maintenance fetch failed:', e?.message || e); }
      const dd = await api.get('/dropdowns');
      const cat = (k) => dd.data.filter((o) => o.category === k).map((o) => o.value);
      setDropdowns({ consumable_type: cat('consumable_type'), consumable_brand: cat('consumable_brand'), consumable_unit: cat('consumable_unit').length ? cat('consumable_unit') : ['pc', 'L', 'kg', 'set', 'box', 'roll', 'm'] });
    } catch (e) {
      // offline — keep showing what we have
      console.warn('Consumables loadAll offline/failed:', e?.message || e);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Online/offline
  useEffect(() => {
    const on = () => { setOnline(true); flushQueue(); loadAll(); };
    const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
    // eslint-disable-next-line
  }, [loadAll]);

  // Drain offline queue
  const flushQueue = useCallback(async () => {
    const queueRaw = localStorage.getItem('consumables_queue');
    if (!queueRaw) return;
    let queue = [];
    try { queue = JSON.parse(queueRaw) || []; } catch (e) { queue = []; }
    if (!queue.length) return;
    const remaining = [];
    for (const item of queue) {
      try { await api.post(item.url, item.body); } catch (e) { remaining.push(item); }
    }
    localStorage.setItem('consumables_queue', JSON.stringify(remaining));
    if (queue.length !== remaining.length) {
      toast({ title: `${queue.length - remaining.length} offline ${queue.length - remaining.length === 1 ? 'entry' : 'entries'} synced` });
    }
  }, [toast]);

  const enqueueOrPost = async (url, body) => {
    if (online) {
      try { await api.post(url, body); return true; } catch (e) { console.warn('Consumables online post failed, will queue:', e?.message || e); /* fall through to queue */ }
    }
    const queueRaw = localStorage.getItem('consumables_queue');
    const queue = (() => { try { return JSON.parse(queueRaw) || []; } catch (e) { return []; } })();
    queue.push({ url, body, queued_at: new Date().toISOString() });
    localStorage.setItem('consumables_queue', JSON.stringify(queue));
    return false;
  };

  const tabSwitch = (t) => { setTab(t); setParams({ tab: t }); };

  // ---------- KPIs ----------
  const pendingReqs = reqs.filter((r) => r.status === 'pending');
  const myAssigned = reqs.filter((r) => r.assigned_to_user_id === user?.id && !['received', 'closed', 'rejected'].includes(r.status));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Consumables / Spare Parts"
        description="Track every drop of oil, every filter, every brake pad — with prices. Request new stock with a one-click Purchase Request that becomes an assignable task."
        actions={
          <div className="flex items-center gap-2">
            {online ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-1 rounded text-xs"><Wifi className="h-3.5 w-3.5" /> Online</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-1 rounded text-xs"><WifiOff className="h-3.5 w-3.5" /> Offline — queued locally</span>
            )}
            <Button size="sm" variant="outline" onClick={() => loadAll()} data-testid="cons-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
            <Button size="sm" onClick={() => setReqOpen(true)} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="cons-new-request-btn"><ShoppingCart className="h-4 w-4 mr-1" /> Request Purchase</Button>
            <Button size="sm" onClick={() => setLogOpen(true)} variant="outline" data-testid="cons-new-log-btn"><Plus className="h-4 w-4 mr-1" /> Log Usage</Button>
          </div>
        }
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Wallet} label="Total spend" value={PHP(summary?.total_spend || 0)} accent="emerald" testId="kpi-spend" />
        <Tile icon={Boxes} label="Items tracked" value={summary?.items_tracked || 0} accent="sky" testId="kpi-items" />
        <Tile icon={Truck} label="Equipment covered" value={summary?.vehicles_tracked || 0} accent="indigo" testId="kpi-vehicles" />
        <Tile icon={ShoppingCart} label="Pending requests" value={pendingReqs.length} accent={pendingReqs.length ? 'amber' : 'slate'} testId="kpi-pending" />
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 border-b">
        {[
          ['overview', 'Overview'],
          ['log', `Usage Log (${logs.length})`],
          ['requests', `Purchase Requests (${reqs.length})`],
        ].map(([k, label]) => (
          <button key={k} onClick={() => tabSwitch(k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? 'border-[hsl(217,91%,24%)] text-[hsl(217,91%,24%)]' : 'border-transparent text-slate-500 hover:text-slate-900'}`} data-testid={`tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab summary={summary} reqs={reqs} myAssigned={myAssigned} vehicles={vehicles} onJump={tabSwitch} />}

      {tab === 'log' && (
        <LogTable
          logs={logs}
          vehicles={vehicles}
          canDelete={canDelete}
          onDelete={async (id) => {
            if (!window.confirm('Delete this entry?')) return;
            await api.delete(`/consumables/${id}`); toast({ title: 'Deleted' }); loadAll();
          }}
          onExport={() => exportCSV(logs, vehicles)}
        />
      )}

      {tab === 'requests' && (
        <RequestsTable
          reqs={reqs} users={users} vehicles={vehicles} maint={maint}
          canApproveReject={canApproveReject} canFulfill={canFulfill} canDelete={canDelete} currentUser={user}
          focusId={params.get('focus')}
          onTransition={(pr, to) => setTransition({ pr, to })}
          onDelete={async (id) => {
            if (!window.confirm('Delete this request?')) return;
            await api.delete(`/purchase-requests/${id}`); toast({ title: 'Deleted' }); loadAll();
          }}
        />
      )}

      {/* Dialogs */}
      <LogDialog
        open={logOpen} onClose={() => setLogOpen(false)} vehicles={vehicles} maint={maint} dropdowns={dropdowns}
        onSubmit={async (body) => {
          const ok = await enqueueOrPost('/consumables', body);
          toast({ title: ok ? 'Usage logged' : 'Saved offline – will sync when online' });
          setLogOpen(false); localStorage.removeItem(DRAFT_KEY('log')); loadAll();
        }}
      />
      <RequestDialog
        open={reqOpen} onClose={() => setReqOpen(false)} vehicles={vehicles} maint={maint} dropdowns={dropdowns} users={users}
        onSubmit={async (body) => {
          const ok = await enqueueOrPost('/purchase-requests', body);
          toast({ title: ok ? 'Purchase request submitted' : 'Saved offline – will sync when online', description: ok ? 'Assigned to purchasing officer.' : null });
          setReqOpen(false); localStorage.removeItem(DRAFT_KEY('request')); loadAll();
        }}
      />
      <TransitionDialog
        ctx={transition} onClose={() => setTransition(null)}
        onDone={async (body) => {
          try {
            await api.post(`/purchase-requests/${transition.pr.id}/transition`, body);
            toast({ title: `Status → ${body.status}` });
            setTransition(null); loadAll();
          } catch (e) {
            toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' });
          }
        }}
      />
    </div>
  );
};

/* ---------------- OVERVIEW (landing content) ---------------- */
const OverviewTab = ({ summary, reqs, myAssigned, vehicles, onJump }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
    <Card className="lg:col-span-2">
      <CardContent className="p-6 space-y-5">
        <div>
          <div className="text-xs uppercase tracking-wider text-[hsl(217,91%,24%)] font-semibold flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Why this page exists</div>
          <h2 className="text-2xl font-bold text-slate-900 mt-1">Stop bleeding money on untracked oil, filters &amp; brake pads.</h2>
          <p className="text-sm text-slate-600 mt-2">Every consumable carries a price. Without a single source of truth you lose warranty coverage, can&rsquo;t diagnose recurring failures, and submit budget forecasts that are wishful thinking. This page gives motor‑pool staff, operators and project managers a fast, offline‑first way to record exactly what was consumed, on which equipment, when, by whom — and how much it cost.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { icon: Wallet, title: 'Cost control', desc: 'See running average and last purchase price for every part. Spot overpriced suppliers in seconds.' },
            { icon: TrendingUp, title: 'Preventive maintenance', desc: 'Watch consumption trends per equipment, schedule replacements before failure.' },
            { icon: FileText, title: 'Warranty & audit', desc: 'Part numbers and timestamps prove genuine parts were installed within warranty windows.' },
            { icon: AlertTriangle, title: 'Failure diagnosis', desc: 'A pattern of 2× brake pad changes on one truck tells a different story than a quiet logbook.' },
            { icon: Truck, title: 'Per‑equipment budget', desc: 'Total spend per asset feeds your TCO reports and replacement decisions.' },
            { icon: ShoppingCart, title: 'Purchase requests as tasks', desc: 'Operators tap "Request" – purchasing officers get a tracked task, not an SMS lost in WhatsApp.' },
          ].map((b) => (
            <div key={b.title} className="p-3 rounded-md border border-slate-200 bg-slate-50/50">
              <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm"><b.icon className="h-4 w-4 text-[hsl(217,91%,24%)]" /> {b.title}</div>
              <p className="text-xs text-slate-600 mt-1">{b.desc}</p>
            </div>
          ))}
        </div>

        <div>
          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700">What you record per transaction</h3>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {['Date', 'Equipment ID', 'Meter / Hours', 'Item name', 'Quantity & unit', 'Unit price', 'Total cost', 'Brand / Part#', 'Technician', 'Work order (optional)', 'Reason', 'Remarks']
              .map((f) => <div key={f} className="px-2 py-1.5 bg-white border border-slate-200 rounded font-medium text-slate-700">{f}</div>)}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700">Purchase Request workflow</h3>
          <ol className="mt-2 space-y-1.5 text-sm text-slate-700 list-decimal list-inside">
            <li><strong>Trigger</strong> – low stock, breakdown, or upcoming preventive job.</li>
            <li><strong>Submit</strong> – item, qty, est. unit price, reason, priority, delivery date, link to equipment / work order.</li>
            <li><strong>Assignment</strong> – goes to the configured purchasing officer / approver as a tracked task.</li>
            <li><strong>Status</strong> – Pending → Approved → Ordered → Received → Closed (or Rejected).</li>
            <li><strong>Auto‑log</strong> – when marked Received, a Consumables log entry is auto‑created with the actual price.</li>
            <li><strong>Notify</strong> – the original requester gets a notification at every transition.</li>
          </ol>
        </div>

        <div>
          <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700">How it connects</h3>
          <pre className="mt-2 text-[11px] leading-relaxed bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto whitespace-pre">
{`  [ Equipment record ]<──link──>[ Consumables log entry ]──auto-creates──[ Cost rollup KPI ]
            │                              ▲                                          │
            │                              │ on "received"                            │
            ▼                              │                                          ▼
   [ Maintenance / Work Order ]──link──[ Purchase Request task ]<───approvers/notifications
                                              │
                                              └─ shares the same QR/serial trail as Mobilization Forms`}
          </pre>
        </div>

        <Alert className="bg-blue-50 border-blue-200 text-blue-900">
          <AlertDescription className="text-xs">
            <strong>Offline‑first.</strong> Both forms work without internet — entries are saved in your device&rsquo;s localStorage and the queue auto‑syncs when you&rsquo;re back online. Keep photos light or skip them entirely.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>

    <div className="space-y-3">
      <Card><CardContent className="p-5">
        <div className="text-xs uppercase tracking-wider text-slate-500">Top items by spend</div>
        {(summary?.top_items?.length || 0) === 0 ? (
          <div className="mt-2 text-xs text-slate-500">No data yet. Log your first usage to see costs roll up here.</div>
        ) : (
          <ul className="mt-2 space-y-1.5 text-sm">
            {summary.top_items.slice(0, 6).map((i) => (
              <li key={`top-${i.item_name}`} className="flex items-center justify-between border-b last:border-0 pb-1.5">
                <div className="min-w-0">
                  <div className="font-medium truncate">{i.item_name}</div>
                  <div className="text-[10px] text-slate-500">qty {i.qty} · last {PHP(i.last_unit_price)}{i.avg_unit_price != null ? ` · avg ${PHP(i.avg_unit_price)}` : ''}</div>
                </div>
                <div className="font-mono font-semibold text-[hsl(217,91%,24%)]">{PHP(i.total_cost)}</div>
              </li>
            ))}
          </ul>
        )}
      </CardContent></Card>

      <Card><CardContent className="p-5">
        <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center justify-between">
          <span>My open tasks</span>
          {myAssigned.length > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{myAssigned.length}</Badge>}
        </div>
        {myAssigned.length === 0 ? (
          <div className="mt-2 text-xs text-slate-500">Nothing assigned. New purchase requests will land here.</div>
        ) : (
          <ul className="mt-2 space-y-2">
            {myAssigned.slice(0, 6).map((r) => (
              <li key={r.id} className="text-sm border-l-2 border-amber-400 pl-2">
                <div className="font-medium truncate">{r.item_name}</div>
                <div className="text-[10px] text-slate-500 font-mono">{r.reference_number} · {r.status}</div>
              </li>
            ))}
          </ul>
        )}
        <Button size="sm" variant="ghost" className="mt-3 text-xs" onClick={() => onJump('requests')} data-testid="overview-jump-requests"><ShoppingCart className="h-3.5 w-3.5 mr-1" /> Open requests →</Button>
      </CardContent></Card>

      <Card><CardContent className="p-5">
        <div className="text-xs uppercase tracking-wider text-slate-500">Templates</div>
        <p className="text-xs text-slate-500 mt-1">Download starter templates for offline use or low‑code import.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => downloadFile('consumables-log-template.csv', LOG_CSV_TEMPLATE, 'text/csv')} data-testid="tpl-log-csv"><Download className="h-3.5 w-3.5 mr-1" /> CSV log</Button>
          <Button size="sm" variant="outline" onClick={() => downloadFile('consumables-log-template.json', JSON.stringify(LOG_JSON_TEMPLATE, null, 2), 'application/json')} data-testid="tpl-log-json"><Download className="h-3.5 w-3.5 mr-1" /> JSON log</Button>
          <Button size="sm" variant="outline" onClick={() => downloadFile('purchase-request-template.json', JSON.stringify(PR_JSON_TEMPLATE, null, 2), 'application/json')} data-testid="tpl-pr-json"><Download className="h-3.5 w-3.5 mr-1" /> JSON request</Button>
        </div>
      </CardContent></Card>
    </div>
  </div>
);

/* ---------------- USAGE LOG TABLE ---------------- */
const LogTable = ({ logs, vehicles, canDelete, onDelete, onExport }) => {
  const veh = (id) => vehicles.find((v) => v.id === id);
  return (
    <Card><CardContent className="p-0">
      <div className="flex items-center justify-between p-3 border-b">
        <div className="text-sm text-slate-600">{logs.length} entries</div>
        <Button size="sm" variant="outline" onClick={onExport} disabled={logs.length === 0} data-testid="log-export-btn"><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left text-slate-600">
            <tr>
              <th className="p-3">Ref</th><th className="p-3">Date</th><th className="p-3">Equipment</th>
              <th className="p-3">Item</th><th className="p-3">Qty</th><th className="p-3">Unit Price</th>
              <th className="p-3">Total</th><th className="p-3">Reason</th><th className="p-3">Tech</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {logs.length === 0 ? (
              <tr><td colSpan={10} className="p-10 text-center text-slate-500">No usage logs yet. Click <strong>Log Usage</strong> above to start tracking.</td></tr>
            ) : logs.map((l) => {
              const v = veh(l.vehicle_id);
              return (
                <tr key={l.id} className="table-row-hover" data-testid={`log-row-${l.id}`}>
                  <td className="p-3 font-mono text-xs">{l.reference_number}</td>
                  <td className="p-3 text-xs">{formatDate(l.log_date)}</td>
                  <td className="p-3">{v ? <div><div className="font-mono text-xs font-semibold">{v.equipment_code}</div><div className="text-[10px] text-slate-500">{v.description}</div></div> : <span className="text-slate-400 text-xs">—</span>}</td>
                  <td className="p-3">
                    <div className="font-medium">{l.item_name}</div>
                    <div className="text-[10px] text-slate-500">{[l.brand, l.part_number].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td className="p-3 text-xs">{l.quantity} {l.unit}</td>
                  <td className="p-3 font-mono text-xs">{PHP(l.unit_price)}</td>
                  <td className="p-3 font-mono text-xs font-semibold text-[hsl(217,91%,24%)]">{PHP(l.total_cost)}</td>
                  <td className="p-3 text-xs"><Badge variant="secondary" className="text-[10px]">{(l.reason || '').replace('_', ' ')}</Badge></td>
                  <td className="p-3 text-xs">{l.technician || '—'}</td>
                  <td className="p-3 text-right">
                    {canDelete && <Button size="sm" variant="ghost" onClick={() => onDelete(l.id)} data-testid={`log-delete-${l.id}`}><XCircle className="h-4 w-4 text-red-600" /></Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CardContent></Card>
  );
};

/* ---------------- PURCHASE REQUESTS TABLE ---------------- */
const RequestsTable = ({ reqs, users, vehicles, maint, canApproveReject, canFulfill, canDelete, currentUser, focusId, onTransition, onDelete }) => {
  const userById = (id) => users.find((u) => u.id === id);
  const vehById = (id) => vehicles.find((v) => v.id === id);
  const moById = (id) => maint.find((m) => m.id === id);
  const next = (st) => NEXT_LABEL[st];

  return (
    <Card><CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left text-slate-600">
            <tr>
              <th className="p-3">Ref</th><th className="p-3">Item</th><th className="p-3">Qty</th>
              <th className="p-3">Est. Total</th><th className="p-3">Priority</th>
              <th className="p-3">Status</th><th className="p-3">Linked to</th>
              <th className="p-3">Assignee</th><th className="p-3">Need by</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {reqs.length === 0 ? (
              <tr><td colSpan={10} className="p-10 text-center text-slate-500">No purchase requests yet. Click <strong>Request Purchase</strong> to create the first one.</td></tr>
            ) : reqs.map((r) => {
              const v = vehById(r.vehicle_id); const mo = moById(r.work_order_id);
              const nx = next(r.status);
              const canDoNext = nx && (
                (nx.to === 'approved' && canApproveReject) ||
                (nx.to === 'rejected' && canApproveReject) ||
                ((nx.to === 'ordered' || nx.to === 'received' || nx.to === 'closed') && (canFulfill || r.assigned_to_user_id === currentUser?.id))
              );
              return (
                <tr key={r.id} className={`table-row-hover ${focusId === r.id ? 'bg-amber-50/60' : ''}`} data-testid={`req-row-${r.id}`}>
                  <td className="p-3 font-mono text-xs">{r.reference_number}</td>
                  <td className="p-3">
                    <div className="font-medium">{r.item_name}</div>
                    <div className="text-[10px] text-slate-500">{[r.brand, r.part_number].filter(Boolean).join(' · ')} · {(r.reason || '').replace('_', ' ')}</div>
                  </td>
                  <td className="p-3 text-xs">{r.quantity} {r.unit}</td>
                  <td className="p-3 font-mono text-xs font-semibold">{PHP(r.estimated_total)}</td>
                  <td className="p-3"><Badge className={`text-[10px] ${r.priority === 'urgent' ? 'bg-red-100 text-red-800' : r.priority === 'high' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>{r.priority}</Badge></td>
                  <td className="p-3"><Badge className={`text-[10px] border ${STATUS_TONE[r.status]}`} data-testid={`req-status-${r.id}`}>{r.status}</Badge></td>
                  <td className="p-3 text-[10px]">
                    {v && <div className="font-mono font-semibold text-slate-700">{v.equipment_code}</div>}
                    {mo && <div className="text-slate-500"><LinkIcon className="h-3 w-3 inline" /> {mo.description?.slice(0, 24)}</div>}
                    {!v && !mo && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-3 text-xs">{userById(r.assigned_to_user_id)?.full_name || '—'}</td>
                  <td className="p-3 text-xs">{r.requested_delivery_date ? formatDate(r.requested_delivery_date) : '—'}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {canDoNext && nx && (
                      <Button size="sm" onClick={() => onTransition(r, nx.to)} className={`${nx.tone} text-white text-xs mr-1`} data-testid={`req-next-${r.id}`}>
                        <nx.icon className="h-3.5 w-3.5 mr-1" /> {nx.label}
                      </Button>
                    )}
                    {canApproveReject && r.status === 'pending' && (
                      <Button size="sm" variant="ghost" onClick={() => onTransition(r, 'rejected')} data-testid={`req-reject-${r.id}`}><XCircle className="h-4 w-4 text-red-600" /></Button>
                    )}
                    {canDelete && <Button size="sm" variant="ghost" onClick={() => onDelete(r.id)} data-testid={`req-delete-${r.id}`}><XCircle className="h-4 w-4 text-slate-400" /></Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CardContent></Card>
  );
};

/* ---------------- DIALOGS ---------------- */
const useDraft = (key, initial) => {
  const [data, setData] = useState(() => {
    try { const d = localStorage.getItem(key); return d ? { ...initial, ...JSON.parse(d) } : initial; } catch (e) { return initial; }
  });
  const timer = useRef(null);
  const set = (k, v) => setData((p) => {
    const next = { ...p, [k]: v };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => localStorage.setItem(key, JSON.stringify(next)), 600);
    return next;
  });
  const reset = () => { localStorage.removeItem(key); setData(initial); };
  return [data, set, reset, setData];
};

const LogDialog = ({ open, onClose, vehicles, maint, dropdowns, onSubmit }) => {
  const initial = { log_date: new Date().toISOString().slice(0, 10), vehicle_id: '', work_order_id: '', item_name: '', item_type: '', brand: '', part_number: '', unit: 'pc', quantity: 1, unit_price: 0, reason: 'preventive', technician: '', remarks: '' };
  const [form, setField, reset] = useDraft(DRAFT_KEY('log'), initial);
  const total = useMemo(() => Number(form.quantity || 0) * Number(form.unit_price || 0), [form.quantity, form.unit_price]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5" /> Log Consumable / Spare Part Usage</DialogTitle>
          <DialogDescription>Capture exactly what was consumed, when, on which equipment, and how much it cost. Auto‑saves as a draft.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date *</Label><Input type="date" value={form.log_date} onChange={(e) => setField('log_date', e.target.value)} data-testid="log-date" /></div>
          <div><Label>Meter / Hours</Label><Input type="number" value={form.meter_reading || ''} onChange={(e) => setField('meter_reading', e.target.value ? Number(e.target.value) : null)} data-testid="log-meter" /></div>

          <div className="col-span-2"><Label>Equipment</Label>
            <Select value={form.vehicle_id || 'none'} onValueChange={(v) => setField('vehicle_id', v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="log-vehicle"><SelectValue placeholder="Select equipment (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.equipment_code} · {v.description}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div><Label>Item / Part name *</Label><Input value={form.item_name} onChange={(e) => setField('item_name', e.target.value)} placeholder="e.g. Engine oil 15W-40" data-testid="log-item" /></div>
          <div><Label>Type</Label><Combobox value={form.item_type} options={dropdowns.consumable_type} onChange={(v) => setField('item_type', v)} placeholder="oil, filter, tire…" /></div>

          <div><Label>Brand</Label><Combobox value={form.brand} options={dropdowns.consumable_brand} onChange={(v) => setField('brand', v)} placeholder="Shell, Bendix…" /></div>
          <div><Label>Part #</Label><Input value={form.part_number} onChange={(e) => setField('part_number', e.target.value)} data-testid="log-partno" /></div>

          <div><Label>Quantity *</Label><Input type="number" step="0.01" value={form.quantity} onChange={(e) => setField('quantity', Number(e.target.value))} data-testid="log-qty" /></div>
          <div><Label>Unit</Label><Combobox value={form.unit} options={dropdowns.consumable_unit} onChange={(v) => setField('unit', v)} placeholder="pc, L, kg…" /></div>

          <div><Label>Unit Price *</Label><Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setField('unit_price', Number(e.target.value))} data-testid="log-price" /></div>
          <div><Label>Total Cost (auto)</Label><Input value={PHP(total)} readOnly className="bg-slate-50 font-mono" data-testid="log-total" /></div>

          <div><Label>Reason</Label>
            <Select value={form.reason} onValueChange={(v) => setField('reason', v)}>
              <SelectTrigger data-testid="log-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Technician</Label><Input value={form.technician} onChange={(e) => setField('technician', e.target.value)} data-testid="log-tech" /></div>

          <div className="col-span-2"><Label>Work Order (optional)</Label>
            <Select value={form.work_order_id || 'none'} onValueChange={(v) => setField('work_order_id', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Link to maintenance work order" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {maint.map((m) => <SelectItem key={m.id} value={m.id}>{(m.description || 'WO').slice(0, 50)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setField('remarks', e.target.value)} data-testid="log-remarks" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => reset()}>Clear draft</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit({ ...form, total_cost: total })} disabled={!form.item_name || !form.log_date} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="log-submit"><Save className="h-4 w-4 mr-1" /> Save Entry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RequestDialog = ({ open, onClose, vehicles, maint, dropdowns, users, onSubmit }) => {
  const initial = { item_name: '', item_type: '', brand: '', part_number: '', quantity: 1, unit: 'pc', estimated_unit_price: 0, reason: 'low_stock', priority: 'medium', requested_delivery_date: '', vehicle_id: '', work_order_id: '', assigned_to_user_id: '', notes: '' };
  const [form, setField, reset] = useDraft(DRAFT_KEY('request'), initial);
  const total = useMemo(() => Number(form.quantity || 0) * Number(form.estimated_unit_price || 0), [form.quantity, form.estimated_unit_price]);
  const purchasingOfficers = users.filter((u) => ['admin', 'super_admin', 'supervisor', 'approver'].includes(u.role));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Request to Purchase</DialogTitle>
          <DialogDescription>Becomes an assignable task for the purchasing officer / fleet manager.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Item / Part name *</Label><Input value={form.item_name} onChange={(e) => setField('item_name', e.target.value)} placeholder="e.g. Brake pad set, front, EXC-001" data-testid="req-item" /></div>
          <div><Label>Type</Label><Combobox value={form.item_type} options={dropdowns.consumable_type} onChange={(v) => setField('item_type', v)} placeholder="oil, filter, tire…" /></div>
          <div><Label>Brand</Label><Combobox value={form.brand} options={dropdowns.consumable_brand} onChange={(v) => setField('brand', v)} placeholder="Bendix, Bridgestone…" /></div>

          <div><Label>Quantity *</Label><Input type="number" step="0.01" value={form.quantity} onChange={(e) => setField('quantity', Number(e.target.value))} data-testid="req-qty" /></div>
          <div><Label>Unit</Label><Combobox value={form.unit} options={dropdowns.consumable_unit} onChange={(v) => setField('unit', v)} placeholder="pc, L, set…" /></div>

          <div><Label>Estimated Unit Price *</Label><Input type="number" step="0.01" value={form.estimated_unit_price} onChange={(e) => setField('estimated_unit_price', Number(e.target.value))} data-testid="req-price" /></div>
          <div><Label>Estimated Total (auto)</Label><Input value={PHP(total)} readOnly className="bg-slate-50 font-mono" data-testid="req-total" /></div>

          <div><Label>Reason</Label>
            <Select value={form.reason} onValueChange={(v) => setField('reason', v)}>
              <SelectTrigger data-testid="req-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{PR_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setField('priority', v)}>
              <SelectTrigger data-testid="req-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div><Label>Requested Delivery Date</Label><Input type="date" value={form.requested_delivery_date} onChange={(e) => setField('requested_delivery_date', e.target.value)} data-testid="req-delivery" /></div>
          <div><Label>Link to Equipment</Label>
            <Select value={form.vehicle_id || 'none'} onValueChange={(v) => setField('vehicle_id', v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="req-vehicle"><SelectValue placeholder="(optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.equipment_code} · {v.description}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2"><Label>Link to Work Order (optional)</Label>
            <Select value={form.work_order_id || 'none'} onValueChange={(v) => setField('work_order_id', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Link to maintenance work order" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {maint.map((m) => <SelectItem key={m.id} value={m.id}>{(m.description || 'WO').slice(0, 60)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2"><Label>Assign To (optional — defaults to first approver)</Label>
            <Select value={form.assigned_to_user_id || 'none'} onValueChange={(v) => setField('assigned_to_user_id', v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="req-assignee"><SelectValue placeholder="Auto-assign" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Auto-assign to purchasing officer —</SelectItem>
                {purchasingOfficers.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setField('notes', e.target.value)} data-testid="req-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => reset()}>Clear draft</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit({ ...form, estimated_total: total })} disabled={!form.item_name} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="req-submit"><Send className="h-4 w-4 mr-1" /> Submit Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TransitionDialog = ({ ctx, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [actualUnitPrice, setActualUnitPrice] = useState('');
  const [recQty, setRecQty] = useState('');
  useEffect(() => { if (!ctx) { setNote(''); setPoNumber(''); setActualUnitPrice(''); setRecQty(''); } else { setRecQty(ctx.pr.quantity); setActualUnitPrice(ctx.pr.estimated_unit_price); } }, [ctx]);
  if (!ctx) return null;
  const { pr, to } = ctx;
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move to {to}</DialogTitle>
          <DialogDescription>{pr.reference_number} — {pr.item_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {to === 'ordered' && (
            <div><Label>PO Number</Label><Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="PO-2026-001" data-testid="trans-po" /></div>
          )}
          {to === 'received' && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Received Quantity</Label><Input type="number" value={recQty} onChange={(e) => setRecQty(Number(e.target.value))} data-testid="trans-recqty" /></div>
              <div><Label>Actual Unit Price</Label><Input type="number" value={actualUnitPrice} onChange={(e) => setActualUnitPrice(Number(e.target.value))} data-testid="trans-actualprice" /></div>
            </div>
          )}
          <div><Label>Note (optional)</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="trans-note" /></div>
          {to === 'received' && (
            <Alert className="bg-emerald-50 border-emerald-200 text-emerald-900">
              <AlertDescription className="text-xs">A Consumables log entry will be auto-created on receipt for cost tracking.</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onDone({ status: to, note, po_number: poNumber || undefined, actual_unit_price: actualUnitPrice ? Number(actualUnitPrice) : undefined, received_quantity: recQty ? Number(recQty) : undefined, actual_total: actualUnitPrice && recQty ? Number(actualUnitPrice) * Number(recQty) : undefined })}
            className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"
            data-testid="trans-confirm"
          >Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- KPI Tile ---------------- */
const Tile = ({ icon: Icon, label, value, accent, testId }) => {
  const tone = { emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200', sky: 'bg-sky-50 text-sky-700 border-sky-200', indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200', amber: 'bg-amber-50 text-amber-700 border-amber-200', slate: 'bg-slate-50 text-slate-700 border-slate-200' }[accent];
  return (
    <div className="rounded-lg border bg-white p-3 flex items-center gap-3 border-slate-200" data-testid={testId}>
      <div className={`h-10 w-10 rounded-md grid place-items-center border ${tone}`}><Icon className="h-5 w-5" /></div>
      <div>
        <div className="text-[11px] text-slate-500">{label}</div>
        <div className="text-xl font-bold text-slate-900 leading-tight">{value}</div>
      </div>
    </div>
  );
};

/* ---------------- Helpers ---------------- */
const downloadFile = (filename, content, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 800);
};

const exportCSV = (logs, vehicles) => {
  const veh = (id) => vehicles.find((v) => v.id === id)?.equipment_code || '';
  const headers = ['reference_number', 'log_date', 'equipment_code', 'item_name', 'item_type', 'brand', 'part_number', 'quantity', 'unit', 'unit_price', 'total_cost', 'reason', 'technician', 'remarks'];
  const rows = logs.map((l) => headers.map((h) => {
    const val = h === 'equipment_code' ? veh(l.vehicle_id) : l[h] ?? '';
    return `"${String(val).replace(/"/g, '""')}"`;
  }).join(','));
  downloadFile('consumables-log.csv', [headers.join(','), ...rows].join('\n'), 'text/csv');
};

const LOG_CSV_TEMPLATE = `log_date,equipment_code,meter_reading,item_name,item_type,brand,part_number,unit,quantity,unit_price,total_cost,reason,technician,work_order_id,remarks
2026-02-10,EXC-001,1235.5,Engine Oil 15W-40,oil,Shell,SH-15W40-5L,L,5,350,1750,preventive,Carlos M.,,Routine 250-hr oil change
2026-02-12,DTR-001,,Air Filter,filter,Donaldson,DON-AF-5102,pc,1,1200,1200,scheduled,Pedro S.,,`;

const LOG_JSON_TEMPLATE = [{
  log_date: '2026-02-10',
  vehicle_id: '<equipment-uuid>',
  meter_reading: 1235.5,
  item_name: 'Engine Oil 15W-40',
  item_type: 'oil',
  brand: 'Shell',
  part_number: 'SH-15W40-5L',
  unit: 'L',
  quantity: 5,
  unit_price: 350,
  total_cost: 1750,
  currency: 'PHP',
  reason: 'preventive',
  technician: 'Carlos M.',
  work_order_id: '<optional-maintenance-uuid>',
  remarks: 'Routine 250-hr oil change',
}];

const PR_JSON_TEMPLATE = [{
  item_name: 'Brake pads (front)',
  item_type: 'brake_pad',
  brand: 'Bendix',
  part_number: 'MKD-9123',
  quantity: 2,
  unit: 'set',
  estimated_unit_price: 2500,
  estimated_total: 5000,
  currency: 'PHP',
  reason: 'breakdown',
  priority: 'high',
  requested_delivery_date: '2026-02-20',
  vehicle_id: '<equipment-uuid>',
  work_order_id: '<optional-maintenance-uuid>',
  notes: 'Squealing reported during inspection — replace before next mobilization.',
}];

export default Consumables;
